import { Collapse } from "antd";
import type { CollapseProps } from "antd";
import CollapseExpandIcon from "../CollapseExpandIcon";
import LocalizedContentFields, { LocalizedContentChanges } from "../LocalizedContentFields";
import { Culture, VolunteersAboutRules } from "../../context/VolunteersAboutContext/constants";
import { getAboutSectionKey, getAboutSectionLabel, VolunteersAboutFieldTexts, VolunteersAboutImageUpload } from "./constants";

export interface VolunteersAboutLocalizableValues {
	culture: Culture;
	title: string;
	description: string;
	imageUrl?: string;
}

export const applyAboutLocalizableChanges = <T extends { culture: Culture }>(
	localizables: T[],
	culture: Culture,
	changes: LocalizedContentChanges
): T[] => localizables.map(item => (item.culture === culture ? { ...item, ...changes } : item));

interface VolunteersAboutFormProps {
	localizables: VolunteersAboutLocalizableValues[];
	// On the add form every field is required; the update form only sends what changed.
	isRequired: boolean;
	onLocalizableChange: (culture: Culture, changes: LocalizedContentChanges) => void;
}

// One "Hakkında - <language>" section per culture with its title, description and image.
const VolunteersAboutForm = ({ localizables, isRequired, onLocalizableChange }: VolunteersAboutFormProps) => {
	const items: CollapseProps["items"] = localizables.map(localizable => ({
		key: getAboutSectionKey(localizable.culture),
		label: getAboutSectionLabel(localizable.culture),
		children: (
			<LocalizedContentFields
				texts={VolunteersAboutFieldTexts[localizable.culture]}
				title={localizable.title}
				description={localizable.description}
				imageUrl={localizable.imageUrl}
				titleMaxLength={VolunteersAboutRules.titleMaxLength}
				descriptionMaxLength={VolunteersAboutRules.descriptionMaxLength}
				imageUpload={VolunteersAboutImageUpload}
				isRequired={isRequired}
				onChange={changes => onLocalizableChange(localizable.culture, changes)}
			/>
		),
	}));

	return (
		<Collapse
			items={items}
			expandIconPosition="end"
			bordered={false}
			ghost
			expandIcon={CollapseExpandIcon}
			defaultActiveKey={localizables.map(localizable => getAboutSectionKey(localizable.culture))}
		/>
	);
};

export default VolunteersAboutForm;
